import React from 'react';

import {trackEvent} from 'analytics/Analytics';
import {HOMEPAGE_ANALYTICS_ACTIONS} from 'constants/analytics-products/HomepageAnalyticsConstants';
import ModelTypeConstants from 'constants/ModelTypeConstants';
import {ReviewSummary} from 'constants/SourceConstants';
import usePreference from 'ui-library/hooks/usePreference';

import {HomepagePreferences} from '../HomepageConstants';
import {
  DEFAULT_REVIEW_STATUS_LABEL,
  ReviewItemStatusLabel,
  REVIEW_ITEM_STATUS_LABELS,
  REVIEW_SUMMARY_COUNT_KEYS,
} from './MyWorkConstants';
import {MyWorkRowsFooterAction} from './MyWorkRowsList';
import {REVIEW_STATUS_METADATA} from './MyWorkStatusMetadata';
import MyWorkStatusTabs, {MyWorkStatusTabData} from './MyWorkStatusTabs';
import {getReviewStatusCount, getSelectedReviewSummaryIds} from './MyWorkUtils';
import {openMyWorkReviewableStatusSearch} from './openMyWorkSearch';

interface ReviewableRowsRenderProps {
  footerAction?: MyWorkRowsFooterAction;
  selectedIds: Array<string> | null;
  totalCount: number;
}

interface ReviewableMyWorkContentProps {
  ariaLabel: string;
  entityTypeLabel: string;
  modelTypes: Array<ModelTypeConstants>;
  preferenceKey: HomepagePreferences;
  reviewSummary: ReviewSummary | null;
  renderRows: (props: ReviewableRowsRenderProps) => JSX.Element;
}

/** Shared reviewable work queue with status tabs and status-specific search. */
export default function ReviewableMyWorkContent({
  ariaLabel,
  entityTypeLabel,
  modelTypes,
  preferenceKey,
  reviewSummary,
  renderRows,
}: ReviewableMyWorkContentProps): JSX.Element {
  const [preferredStatus, setSelectedStatus] = usePreference<ReviewItemStatusLabel | undefined>(
    preferenceKey
  );
  const selectedStatus = preferredStatus ?? DEFAULT_REVIEW_STATUS_LABEL;
  const totalCount =
    reviewSummary == null
      ? 0
      : REVIEW_SUMMARY_COUNT_KEYS.reduce((total, key) => total + (reviewSummary[key] ?? 0), 0);
  const hasWork = totalCount > 0;
  const statuses: Array<MyWorkStatusTabData<ReviewItemStatusLabel>> = REVIEW_ITEM_STATUS_LABELS.map(
    (statusLabel) => ({
      ...REVIEW_STATUS_METADATA[statusLabel],
      count: getReviewStatusCount(reviewSummary, statusLabel),
    })
  );

  return (
    <>
      {hasWork && (
        <MyWorkStatusTabs
          ariaLabel={ariaLabel}
          selectedStatus={selectedStatus}
          statuses={statuses}
          onStatusChange={(statusLabel) => {
            setSelectedStatus(statusLabel);
          }}
        />
      )}
      {renderRows({
        selectedIds: getSelectedReviewSummaryIds(reviewSummary, [selectedStatus]),
        totalCount,
        footerAction: hasWork
          ? {
              label: `See ${selectedStatus} ${entityTypeLabel} in search`,
              onClick: () => {
                trackEvent(HOMEPAGE_ANALYTICS_ACTIONS.CLICKED_MY_WORK_SEE_ALL_BUTTON);
                openMyWorkReviewableStatusSearch(modelTypes, selectedStatus);
              },
            }
          : undefined,
      })}
    </>
  );
}
